/**
 * ハンバーガーメニューの開閉を制御するモジュール
 * スマホ時にメニューの表示/非表示を切り替え、背景スクロールをロックする
 *
 * @example
 * // 使用例
 * import { initHamburgerMenu } from './module/cmn-header';
 * initHamburgerMenu();
 */

/** メニューが開いているかどうかの状態 */
let isMenuOpen = false;

/**
 * 背景のスクロールをロックする
 * メニューが開いている間、body要素のスクロールを無効化する
 */
const lockScroll = (): void => {
  document.body.style.overflow = 'hidden';
};

/**
 * 背景のスクロールロックを解除する
 */
const unlockScroll = (): void => {
  document.body.style.overflow = '';
};

/**
 * メニューを開く
 * @param headerInner - header__inner要素
 * @param headerHams - header__ham要素のリスト
 */
const openMenu = (headerInner: HTMLElement, headerHams: NodeListOf<HTMLElement>): void => {
  isMenuOpen = true;
  headerInner.style.top = '0';
  headerHams.forEach((ham) => ham.classList.add('is-active'));
  lockScroll();
};

/**
 * メニューを閉じる
 * @param headerInner - header__inner要素
 * @param headerHams - header__ham要素のリスト
 */
const closeMenu = (headerInner: HTMLElement, headerHams: NodeListOf<HTMLElement>): void => {
  isMenuOpen = false;
  headerInner.style.top = '100%';
  headerHams.forEach((ham) => ham.classList.remove('is-active'));
  unlockScroll();
};

/**
 * ハンバーガーメニューを初期化する
 * クリックイベントを設定し、メニューの開閉を制御する
 *
 * @example
 * // DOMContentLoaded後に呼び出す
 * document.addEventListener('DOMContentLoaded', () => {
 *   initHamburgerMenu();
 * });
 */
export const initHamburgerMenu = (): void => {
  const headerHams = document.querySelectorAll<HTMLElement>('.header__ham');
  const headerInner = document.querySelector<HTMLElement>('.header__inner');

  if (headerHams.length === 0 || !headerInner) {
    console.warn('ハンバーガーメニューの要素が見つからないよ！');
    return;
  }

  // メニュー内をスクロール可能にする
  headerInner.style.overflowY = 'auto';
  headerInner.style.height = '100vh';

  // 全ハンバーガーボタンにクリックイベントを設定
  headerHams.forEach((ham) => {
    ham.addEventListener('click', () => {
      if (isMenuOpen) {
        closeMenu(headerInner, headerHams);
      } else {
        openMenu(headerInner, headerHams);
      }
    });
  });

  // メニュー内のリンクをクリックしたらメニューを閉じる
  const menuLinks = headerInner.querySelectorAll('a');
  menuLinks.forEach((link) => {
    link.addEventListener('click', () => {
      closeMenu(headerInner, headerHams);
    });
  });

  // ESCキーでメニューを閉じる
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && isMenuOpen) {
      closeMenu(headerInner, headerHams);
    }
  });
};

// 自動初期化
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initHamburgerMenu);
} else {
  initHamburgerMenu();
}